/**
 * @file Powerup drops and collection (GDD §Fase 3 — powerups).
 *
 * Two responsibilities, both driven by data:
 *  - roll the drop table when something dies and spawn the powerup;
 *  - apply a powerup when the player walks over it.
 *
 * What a powerup *does* lives in the `_handlers` table, keyed on
 * `definition.kind`. A new powerup that reuses an existing kind needs no code.
 *
 * Weapons are the exception: they carry an `InteractionTarget` and belong to
 * {@link ./InteractionSystem.js}, because taking one costs you the gun you are
 * holding and so must be asked for.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Pickup,
  Transform,
  PlayerControlled,
  Health,
  StatusEffects,
  Weapon,
  InteractionTarget,
  Renderable,
  AIBrain,
  RangedAI
} from '../components/index.js';
import { createPickup } from '../entities/PickupEntity.js';
import { DROPS, rollPowerup } from '../config/Powerups.js';
import { TimedStatus } from '../components/StatusEffects.js';
import { Events } from '../core/EventBus.js';
import { isWeaponDepleted } from '../combat/ammoPolicy.js';

/** Recolours a mesh's materials red to flag a weapon with nothing left in it. */
const DEPLETED_TINT_COLOR = 0xff2a2a;

/**
 * Tints (or un-tints) every mesh under `object` red, caching the clone on each
 * node so repeated calls — including on a mesh recycled from the prefab pool
 * for a different, non-depleted drop later — never touch the shared source
 * material the model library builds once per weapon key.
 * @param {import('three').Object3D} object Root of the acquired weapon mesh.
 * @param {boolean} depleted Whether the tint should be showing.
 * @returns {void}
 */
function setDepletedTint(object, depleted) {
  object.traverse((node) => {
    if (!node.isMesh) return;
    if (!node.userData.baseMaterial) node.userData.baseMaterial = node.material;
    if (!depleted) {
      node.material = node.userData.baseMaterial;
      return;
    }
    if (!node.userData.depletedMaterial) {
      node.userData.depletedMaterial = tintMaterial(node.userData.baseMaterial);
    }
    node.material = node.userData.depletedMaterial;
  });
}

/**
 * @param {import('three').Material | import('three').Material[]} material Source material(s).
 * @returns {import('three').Material | import('three').Material[]} Independent red clone(s).
 */
function tintMaterial(material) {
  if (Array.isArray(material)) return material.map((entry) => tintMaterial(entry));

  const clone = material.clone();
  if ('color' in clone) clone.color = new THREE.Color(DEPLETED_TINT_COLOR);
  if ('emissive' in clone) {
    clone.emissive = new THREE.Color(DEPLETED_TINT_COLOR);
    clone.emissiveIntensity = Math.max(clone.emissiveIntensity ?? 0, 0.6);
  }
  return clone;
}

export class PickupSystem extends System {
  static priority = 55;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];

    /**
     * Powerup behaviours, keyed by `definition.kind`.
     * @type {Record<string, (player: import('../ecs/Entity.js').Entity,
     *                        definition: import('../config/Powerups.js').PowerupDefinition,
     *                        pickup: import('../components/Pickup.js').Pickup) => void>}
     * @private
     */
    this._handlers = {
      immortal: (player, definition) => {
        const effects = this._statusEffects(player);
        effects.applyTimed(TimedStatus.IMMORTAL, definition.duration);
        this.ctx.events.emit(Events.STATUS_CHANGED, { effects });
      },

      shield: (player, definition) => {
        const effects = this._statusEffects(player);
        effects.shield = Math.max(effects.shield, definition.value);
        effects.shieldRemaining = Math.max(effects.shieldRemaining, definition.duration);
        this.ctx.events.emit(Events.STATUS_CHANGED, { effects });
      },

      buff: (player, definition) => {
        const effects = this._statusEffects(player);
        effects.apply(definition.stat, definition.multiplier, definition.duration);
        this.ctx.events.emit(Events.STATUS_CHANGED, { effects });
      },

      ammo: (player, definition) => {
        const effects = this._statusEffects(player);
        effects.applyTimed(TimedStatus.UNLIMITED_AMMO, definition.duration);
        player.get(Weapon)?.cancelReload();
        this.ctx.events.emit(Events.STATUS_CHANGED, { effects });
      }
    };

    /** @type {number} Elapsed time of the last weapon pickup. @private */
    this._lastWeaponPickup = -Infinity;
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DIED, (payload) => this._rollDrop(payload))
    );
  }

  /**
   * @param {{ entity: import('../ecs/Entity.js').Entity }} payload Death event.
   * @returns {void}
   * @private
   */
  _rollDrop({ entity }) {
    if (!entity.has(AIBrain) && !entity.has(RangedAI)) return;

    const chance = DROPS.chance + (entity.dropBonus ?? 0);
    if (Math.random() > chance) return;

    const transform = entity.get(Transform);
    if (!transform) return;

    createPickup(this.ctx, transform.position.x, transform.position.z, rollPowerup());
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const pickups = this.world.query(Pickup, Transform);
    if (pickups.length === 0) return;

    const player = this._findPlayer();
    const playerPosition = player?.get(Transform).position;
    const radiusSq = DROPS.pickupRadius * DROPS.pickupRadius;

    for (const entity of pickups) {
      // Destruction is deferred to the end of the frame, so something already
      // claimed this frame — an enemy scavenging a weapon — is still listed.
      if (!entity.alive) continue;

      const pickup = entity.get(Pickup);
      const transform = entity.get(Transform);

      // Idle animation lives here, not in the render system: it is state, and
      // the render system only ever mirrors state.
      pickup.phase += dt * DROPS.bobSpeed;
      transform.position.y = DROPS.hoverHeight + Math.sin(pickup.phase) * DROPS.bobHeight;
      transform.rotationY += dt * DROPS.spinSpeed;

      // A floor weapon's ammo never changes on its own, so this only needs to
      // run once — but it must run every time regardless of pool history, since
      // the mesh this drop acquired may have been tinted red by a previous
      // occupant of the same prefab slot.
      if (!pickup.depletedTintChecked && pickup.weapon) {
        const renderable = entity.get(Renderable);
        if (renderable) setDepletedTint(renderable.object, isWeaponDepleted(pickup.weapon));
        pickup.depletedTintChecked = true;
      }

      // Interactables are never vacuumed up: they wait for their key press.
      if (!playerPosition || entity.has(InteractionTarget)) continue;

      const dx = transform.position.x - playerPosition.x;
      const dz = transform.position.z - playerPosition.z;
      const distanceSq = dx * dx + dz * dz;

      // A swapped-out weapon lands under the player's feet. It only becomes
      // collectible once they walk off it, otherwise standing still would swap
      // the two guns back and forth every frame.
      if (!pickup.armed) {
        if (distanceSq > radiusSq) pickup.armed = true;
        continue;
      }

      if (distanceSq > radiusSq) continue;

      this._collect(player, entity, pickup);
    }
  }

  /**
   * @returns {import('../ecs/Entity.js').Entity | null}
   * @private
   */
  _findPlayer() {
    const players = this.world.query(PlayerControlled, Transform, Health);
    for (const player of players) {
      if (!player.get(Health).dead) return player;
    }
    return null;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} player Player entity.
   * @returns {import('../components/StatusEffects.js').StatusEffects} Status bag.
   * @private
   */
  _statusEffects(player) {
    return player.get(StatusEffects) ?? player.add(new StatusEffects());
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} player Collector.
   * @param {import('../ecs/Entity.js').Entity} entity Pickup entity.
   * @param {import('../components/Pickup.js').Pickup} pickup Pickup data.
   * @returns {void}
   * @private
   */
  _collect(player, entity, pickup) {
    const handler = this._handlers[pickup.definition.kind];
    if (!handler) {
      console.warn(`No handler for powerup kind "${pickup.definition.kind}".`);
      return;
    }

    if (pickup.definition.kind === 'weapon') {
      // Guard against a single step chaining through a pile of guns.
      const now = this.ctx.time.elapsed;
      if (now - this._lastWeaponPickup < DROPS.weaponSwapCooldown) return;
      this._lastWeaponPickup = now;
    }

    handler(player, pickup.definition, pickup);
    this.ctx.events.emit(Events.PICKUP_COLLECTED, {
      entity,
      key: pickup.key,
      definition: pickup.definition
    });
    this.world.destroyEntity(entity);
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}
